import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2 } from "lucide-react";

export function Lightbox({ src, alt, onClose }) {
  useEffect(() => {
    if (!src) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [src, onClose]);

  return createPortal(
    <AnimatePresence>
      {src && (
        <motion.div
          data-testid="lightbox-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] bg-[#1E1B4B]/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
        >
          <button
            type="button"
            data-testid="lightbox-close-btn"
            onClick={onClose}
            aria-label="Close full-size image"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-amber-400 hover:text-[#2E1065] text-white flex items-center justify-center transition-colors"
          >
            <X size={22} />
          </button>
          <motion.img
            data-testid="lightbox-image"
            src={src}
            alt={alt}
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl border-4 border-amber-400/60"
          />
          {alt && (
            <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-xs sm:text-sm text-purple-200 bg-black/30 px-4 py-1.5 rounded-full max-w-[90vw] truncate">
              {alt}
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export function ZoomableImage({ src, alt, className, imgClassName, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(true)}
        aria-label={`View full size: ${alt}`}
        className={`group/zoom relative block w-full cursor-zoom-in overflow-hidden ${className || ""}`}
      >
        <img src={src} alt={alt} className={imgClassName} />
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#2E1065]/80 text-amber-300 text-[11px] font-bold px-3 py-1.5 opacity-0 group-hover/zoom:opacity-100 transition-opacity duration-300 pointer-events-none">
          <Maximize2 size={12} /> View full size
        </span>
      </button>
      <Lightbox src={open ? src : null} alt={alt} onClose={() => setOpen(false)} />
    </>
  );
}
